<template>
    <WebSocketLoadingModal
        :title="__('customers.transactions.request_notice')"
        :show-modal="showModal"
        :internal-support-link="internalSupportLink"
        :soft-timeout="SOFT_TIMEOUT_SECONDS"
        :status-hint="statusHint"
        :error-message="errorMessage"
        @close="closeModal"
    />
</template>

<script setup>
import { __ } from '~@/Composables/translation'
import { route } from '~@/Composables/route'
import WebSocketLoadingModal from '~@/Components/Common/Modals/WebSocketLoadingModal'
import { usePage } from '@inertiajs/vue3'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const SOFT_TIMEOUT_SECONDS = 180
const POLL_INTERVAL_MS = 5000

const DONE_EVENT = '.customers.manual_finance_sync_done'
const FAILED_EVENT = '.customers.manual_finance_sync_failed'

const props = defineProps({
    customer: {
        type: Object,
        default: () => ({})
    },
    showModal: {
        type: Boolean,
        default: false
    },
    internalSupportLink: {
        type: String,
        default: () => { return '' }
    },
    // Id of the queue job request backing this sync. Null when the request row could not be created,
    // in which case we can only rely on the broadcast events.
    syncRequestId: {
        type: Number,
        default: null
    }
})

const emit = defineEmits(['closeModal', 'success'])

const errorMessage = ref(null)
const pollStatus = ref(null)
const pollInterval = ref(null)
const pollInFlight = ref(false)

const statusHint = computed(() => {
    if (pollStatus.value === null) {
        return __('customers.transactions.sync_status.running')
    }

    return pollStatus.value.hasStarted
        ? __('customers.transactions.sync_status.running')
        : __('customers.transactions.sync_status.queued')
})

const stopPolling = () => {
    if (pollInterval.value !== null) {
        clearInterval(pollInterval.value)
        pollInterval.value = null
    }
}

const succeed = () => {
    stopPolling()
    emit('success')
}

const fail = (message) => {
    stopPolling()
    errorMessage.value = message
}

/**
 * The broadcast payload is a FlashMessage, so title, body and the raw error are all optional.
 */
const flashMessageToText = (flashMessage) => {
    return [flashMessage?.title, flashMessage?.body, flashMessage?.error]
        .filter(part => typeof part === 'string' && part !== '')
        .join('\n')
}

const readStatus = async () => {
    if (props.syncRequestId === null || pollInFlight.value) {
        return
    }

    pollInFlight.value = true

    try {
        const { data } = await window.axios.get(
            route('customers.transactions.finance_system_sync_status', {
                customer: props.customer.id,
                queueJobRequest: props.syncRequestId
            })
        )

        pollStatus.value = data

        if (data.isCompleted) {
            succeed()
        } else if (data.isCancelled) {
            fail(__('customers.transactions.sync_status.cancelled'))
        } else if (data.isFailed) {
            fail(data.error || __('customers.transactions.sync_status.failed_unknown'))
        }
    } catch (error) {
        // A failing status request tells us nothing about the job itself, so keep polling and let the
        // broadcast events or the timeout hint take over.
        console.error(error)
    } finally {
        pollInFlight.value = false
    }
}

const channel = window.laravelEcho.private(
    `onecrm.customers.finance.manual_sync.${usePage().props.auth.user.id}`
)

const isForThisCustomer = ({ customerId, tenant }) => {
    return tenant === usePage().props.tenant && customerId === props.customer.id
}

const onSyncDone = (payload) => {
    if (isForThisCustomer(payload)) {
        succeed()
    }
}

const onSyncFailed = (payload) => {
    if (isForThisCustomer(payload)) {
        fail(flashMessageToText(payload.error) || __('customers.transactions.sync_status.failed_unknown'))
    }
}

channel
    .listen(DONE_EVENT, onSyncDone)
    .listen(FAILED_EVENT, onSyncFailed)

const startPolling = () => {
    if (props.syncRequestId === null || pollInterval.value !== null) {
        return
    }

    readStatus()
    pollInterval.value = setInterval(readStatus, POLL_INTERVAL_MS)
}

onMounted(startPolling)

// The modal is opened before the request id is known, and whether Inertia remounts this component or
// only updates its props on the redirect depends on the visit, so cover the latter case too.
watch(() => props.syncRequestId, startPolling)

// This component sits behind a v-if, so without this every open would stack another pair of
// listeners on the channel and a single event would be handled several times.
onUnmounted(() => {
    stopPolling()
    channel.stopListening(DONE_EVENT, onSyncDone)
    channel.stopListening(FAILED_EVENT, onSyncFailed)
})

const closeModal = () => {
    stopPolling()
    emit('closeModal')
}

</script>
